import * as THREE from 'three'
import { OBJLoader } from 'three/addons/loaders/OBJLoader.js'
import { EXRLoader } from 'three/addons/loaders/EXRLoader.js'

export default class SukuScene extends HTMLElement {
  // set by container
  constructor() {
    super()
    this.mobile = window.innerWidth < 778
    this.width = this.mobile ? window.innerWidth : window.innerWidth / 2
    this.height = this.mobile ? window.innerHeight * 0.6 : window.innerHeight - 110
    
    // object child keys
    this.quiltKeys = ['Quilt', 'donna', 'quilt', 'doona_mid', 'doona_low']
    this.fittedKeys = ['fitted_sheet', 'fitted', 'fitted_sheet_mid', 'fitted_sheet_low']
    this.flatKeys = ['loose_sheet', 'top_sheet_mid', 'top_sheet_low']
    this.pillowKeys = [ 'Pillow_1', 'Pillow_2', 'pillow_1', 'pillow_2', 'pillow_mid', 'pillow_mid.1', 'pillow_low', 'pillow_low.1']

    this.options = {
      camera: {
        position: {
          y: 300,
          z: 380
        },
        fov: 40 // change to 35
      },
      lighting: {
        ambient: {
          color: '#FFFFFF',
          intensity: 1.4 * Math.PI
        },
        directional: {
          color: '#FFFFFF',
          intensity: 2.8 * Math.PI
        }
      },
      material: {
        envMapIntensity: 6,
        aoMapIntensity: 1.85,
        roughness: 1.8,
        emissiveIntensity: 0.001,
        displacement: 0
      }
    }

    this.scene = new THREE.Scene()
    this.offset = new THREE.Vector3()
    this.camera = new THREE.PerspectiveCamera(this.options.camera.fov, this.width / this.height, 10, 1000)
    this.cameraHelper = new THREE.CameraHelper(this.camera)
    this.renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true })
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.needsUpdate = true
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap
    this.renderer.shadowMap.renderReverseSided = true
    this.renderer.physicallyCorrectLights = true
    this.renderer.gammaOutput = true
    this.renderer.gammaFactor = 2.2
    this.renderer.outputEncoding = THREE.sRGBEncoding
    this.manager = new THREE.LoadingManager(() => this.loadModel())
    this.bed

    this.ambientLight = new THREE.AmbientLight(this.options.lighting.ambient.color, this.options.lighting.ambient.intensity)
    this.directionalLight = new THREE.DirectionalLight(this.options.lighting.directional.color, this.options.lighting.directional.intensity)

    this.objLoader = new OBJLoader(this.manager)
    this.exrLoader = new EXRLoader(this.manager)
    this.materialLoader = new THREE.MaterialLoader(this.manager)
    this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
    this.textureLoader = new THREE.TextureLoader()
    this.exrCubeRenderTarget

    this.className = 'w-1/2 sticky top-[0px] h-[calc(100vh-100px)] z-[-1] mobile:w-full mobile:h-[calc(60vh)]'

    this.init.bind(this)
    this.animate.bind(this)
    this.render.bind(this)
    this.loadModel.bind(this)

    window.addEventListener('resize', () => {
      this.mobile = window.innerWidth < 778
      this.width = this.mobile ? window.innerWidth : window.innerWidth / 2
      this.height = this.mobile ? window.innerHeight * 0.6 : window.innerHeight - 100
      
      this.camera.aspect = this.width / this.height
      this.camera.updateProjectionMatrix()
      this.renderer.setSize(this.width, this.height)
    })

    window.addEventListener('sceneupdate', this.handleUpdate.bind(this))
  }

  handleUpdate(evt) {
    const selected = evt.detail.selected

    this.updateModel(selected.category, selected.size, evt.detail.textures, evt.detail.textures.style)
  }
  
  connectedCallback() {
    this.init()
  }

  init() {
    // set size of renderer
    this.renderer.setSize(this.width, this.height)
    this.renderer.setPixelRatio( window.devicePixelRatio )

    this.scene.add(this.ambientLight)

    const dlHelper = new THREE.DirectionalLightHelper(this.directionalLight, 200)
    const cameraHelper = new THREE.CameraHelper(this.directionalLight.shadow.camera)

    this.directionalLight.position.set(55, 210, -10)
    this.directionalLight.castShadow = true

    this.directionalLight.shadow.mapSize.x = 1024 // default
    this.directionalLight.shadow.mapSize.y = 1024; // default
    this.directionalLight.shadow.mapSize.width = 4096; // default
    this.directionalLight.shadow.mapSize.height = 4096; // default
    this.directionalLight.shadow.camera.near = 1; // default
    this.directionalLight.shadow.camera.far = 500; // default
    this.directionalLight.shadow.camera.fov = this.options.camera.fov; // default
    this.directionalLight.shadow.camera.scale.setY(28)
    this.directionalLight.shadow.camera.scale.setX(28)
    this.directionalLight.shadow.camera.scale.setZ(28)
    this.directionalLight.shadowCameraTop = 2500
    this.directionalLight.shadowCameraBottom = -2500
    this.directionalLight.shadowCameraRight = 2500
    this.directionalLight.shadowCameraLeft = -2500

    this.scene.add(this.directionalLight)
    // this.scene.add(dlHelper)
    // this.scene.add(cameraHelper)
    
    this.pmremGenerator.compileEquirectangularShader()

    // append renderer to this
    this.appendChild(this.renderer.domElement)

    this.camera.position.z = this.options.camera.position.z
    this.camera.position.y = this.options.camera.position.y 
    this.camera.position.x = 0
     
    this.scene.add(this.camera)
    // this.scene.add(this.cameraHelper)

    this.scene.background = new THREE.Color(0xFFFFFF)

    // load OBJ
    this.objLoader.load(window.__SUKU.bed, (object) => {
      this.bed = object

      this.directionalLight.target = this.bed
    })

    const geometry = new THREE.PlaneGeometry(1000, 1000, 32)
    const planeMaterial = new THREE.MeshBasicMaterial({ color: 0xFFFFFF, side: THREE.FrontSide })
    planeMaterial.toneMapped = false
    const plane = new THREE.Mesh( geometry, planeMaterial )
    plane.receiveShadow = true
    plane.rotateX(-Math.PI / 2)
    plane.position.y = - 8.4

    this.scene.add(plane)
  }

  animate() {
    requestAnimationFrame(() => this.animate())

    const time = Date.now()

    this.offset.x = this.options.camera.position.z * Math.sin( time * 0.00038 );
    this.offset.z = this.options.camera.position.z * Math.cos( time * 0.00038 );

    this.camera.position.copy(this.bed.position).add(this.offset);
    this.camera.lookAt(this.bed.position)
    this.camera.position.setY(this.options.camera.position.y)

    this.renderer.toneMapping = THREE.ACESFilmicToneMapping
    this.renderer.toneMappingExposure = 0.215
    this.renderer.outputEncoding = THREE.sRGBEncoding

    this.render()
  }

  render() {
    this.camera.lookAt(this.scene.position)

    this.renderer.render(this.scene, this.camera)
  }

  getBaseMaterials() {
    const envMap = new EXRLoader().setDataType( THREE.FloatType ).load(window.__SUKU.studio, (texture) => {
      const exrCubeRenderTarget = this.pmremGenerator.fromEquirectangular(texture)
      return exrCubeRenderTarget.texture
    })

    const normalMap = this.textureLoader.load(window.__SUKU.fabric.normal)
    normalMap.wrapS = THREE.RepeatWrapping
    normalMap.wrapT = THREE.RepeatWrapping
    
    const aoMap = this.textureLoader.load(window.__SUKU.fabric.ao)
    aoMap.wrapS = THREE.RepeatWrapping
    aoMap.wrapT = THREE.RepeatWrapping
    
    const roughnessMap = this.textureLoader.load(window.__SUKU.fabric.roughness) 
    roughnessMap.wrapS = THREE.RepeatWrapping
    roughnessMap.wrapT = THREE.RepeatWrapping

    const displacementMap = this.textureLoader.load(window.__SUKU.fabric.displacement) 
    displacementMap.wrapS = THREE.RepeatWrapping
    displacementMap.wrapT = THREE.RepeatWrapping
    
    const baseMaterialOptions = {
      envMap: envMap,
      envMapIntensity: this.options.material.envMapIntensity,
      normalMap: normalMap,
      aoMap: aoMap,
      aoMapIntensity: this.options.material.aoMapIntensity,
      roughnessMap: roughnessMap,
      roughness: this.options.material.roughness,
      displacementMap: displacementMap,
      displacementScale: this.options.material.displacement,
      emissiveIntensity: this.options.emissiveIntensity
    }

    return baseMaterialOptions
  }

  updateModel(category, size, textures, style) {
    const keys = () => {
      switch(category) {
        case 'quilt':
          return this.quiltKeys
        case 'fitted':
        case 'fitted_sheet':
          return this.fittedKeys
        case 'flat':
        case 'flat_sheet':
          return this.flatKeys
        case 'pillowcases':
          return this.pillowKeys
      }
    }

    const baseMaterials = this.getBaseMaterials()

    this.bed.traverse(child => {
      if (child instanceof THREE.Mesh) {
        const name = child.name.toLowerCase()
        
        if (keys().includes(name)) {
          const primary = this.textureLoader.load(textures.main)
          primary.wrapT = THREE.RepeatWrapping
          primary.wrapS = THREE.RepeatWrapping
          primary.encoding = THREE.sRGBEncoding
          primary.needsUpdate = true
          if (keys().includes('quilt')) {
            primary.center.set(0.5, 0.5)
            primary.rotation = THREE.MathUtils.degToRad(90)
            primary.updateMatrix()
          }

          const primaryMaterial = new THREE.MeshStandardMaterial({
            map: primary,
            side: style === 'two-tone' && keys().includes('quilt') ? THREE.BackSide : (keys().includes('quilt') ? THREE.BackSide : THREE.FrontSide),
            ...baseMaterials
          })
          primaryMaterial.map.minFilter = THREE.LinearFilter

          if (style === 'two-tone' && keys().includes('quilt')) {
            const secondary = this.textureLoader.load(textures.alt)
            secondary.wrapT = THREE.RepeatWrapping
            secondary.wrapS = THREE.RepeatWrapping
            secondary.encoding = THREE.sRGBEncoding
            secondary.needsUpdate = true

            const secondaryMaterial = new THREE.MeshStandardMaterial({
              map: secondary,
              side: THREE.BackSide,
              ...baseMaterials
            })
            secondaryMaterial.map.minFilter = THREE.LinearFilter
            
            child.material = [primaryMaterial, secondaryMaterial]
          } else {
            child.material = primaryMaterial
          }
        }

        child.castShadow = true
        child.receiveShadow = true
      }
    })
  }

  loadModel() {
    let pillowcases, flatSheet, quilt, underside = false
    const fittedSheet = this.textureLoader.load(window.__SUKU.initial.fittedSheet)
    fittedSheet.needsUpdate = true
    fittedSheet.encoding = THREE.sRGBEncoding
    fittedSheet.wrapS = true
    fittedSheet.wrapT = true

    if (window.__SUKU.initial.hasOwnProperty('pillowcases')) {
      pillowcases = this.textureLoader.load(window.__SUKU.initial.pillowcases)
      pillowcases.needsUpdate = true
      pillowcases.encoding = THREE.sRGBEncoding
      pillowcases.wrapS = THREE.RepeatWrapping
      pillowcases.wrapT = THREE.RepeatWrapping
    }

    if (window.__SUKU.initial.hasOwnProperty('flatSheet')) {
      flatSheet = this.textureLoader.load(window.__SUKU.initial.flatSheet)
      flatSheet.needsUpdate = true
      flatSheet.encoding = THREE.sRGBEncoding
      flatSheet.wrapS = THREE.RepeatWrapping
      flatSheet.wrapT = THREE.RepeatWrapping
    }
    
    if (window.__SUKU.initial.hasOwnProperty('quilt')) {
      quilt = this.textureLoader.load(window.__SUKU.initial.quilt)
      quilt.needsUpdate = true
      quilt.encoding = THREE.sRGBEncoding
      quilt.wrapS = THREE.RepeatWrapping
      quilt.wrapT = THREE.RepeatWrapping
      quilt.center.set(0.5, 0.5)
      quilt.rotation = THREE.MathUtils.degToRad(90)
    }
    
    if (window.__SUKU.initial.hasOwnProperty('underside')) {
      underside = this.textureLoader.load(window.__SUKU.initial.underside)
      underside.needsUpdate = true
      underside.encoding = THREE.sRGBEncoding
      underside.wrapS = THREE.RepeatWrapping
      underside.wrapT = THREE.RepeatWrapping
    }

    const baseMaterials = this.getBaseMaterials()

    this.bed.traverse(child => {
      if (child instanceof THREE.Mesh) {

        if (this.quiltKeys.includes(child.name)) {
          child.visible = quilt ? true : false
          const topMaterial = new THREE.MeshStandardMaterial({
            map: quilt,
            color: 0xFFFFFF,
            side: THREE.BackSide,
            ...baseMaterials
          })

          topMaterial.map.minFilter = THREE.LinearFilter 

          const bottomMaterial = new THREE.MeshStandardMaterial({
            map: underside ? underside : quilt,
            color: 0xFFFFFF,
            side: THREE.BackSide,
            ...baseMaterials
          })
          bottomMaterial.map.minFilter = THREE.LinearFilter

          child.castShadow = true
          child.receiveShadow = true

          child.material = [topMaterial, bottomMaterial]
        }
        
        if (this.flatKeys.includes(child.name)) {
          child.visible = flatSheet ? true : false
          const lsMaterial = new THREE.MeshStandardMaterial({
            map: flatSheet,
            color: 0xFFFFFF,
            ...baseMaterials
          })
          lsMaterial.map.minFilter = THREE.LinearFilter

          child.castShadow = true
          child.receiveShadow = true
          
          child.material = lsMaterial
        }

        if (this.fittedKeys.includes(child.name)) {
          const fsMaterial = new THREE.MeshStandardMaterial({
            map: fittedSheet,
            color: 0xFFFFFF,
            ...baseMaterials
          })
          fsMaterial.map.minFilter = THREE.LinearFilter

          child.castShadow = true
          child.receiveShadow = true

          child.material = fsMaterial
        }

        if (this.pillowKeys.includes(child.name) && pillowcases) {
          child.visible = pillowcases ? true : false
          const pMaterial = new THREE.MeshStandardMaterial({
            map: pillowcases,
            color: 0xFFFFFF,
            ...baseMaterials
          })
          pMaterial.map.minFilter = THREE.LinearFilter

          child.castShadow = true
          child.receiveShadow = true

          child.material = pMaterial
        }
      }
    })

    this.scene.add(this.bed);

    this.animate()
  }
}

customElements.define('suku-scene', SukuScene)
